import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { AlertTriangle, ArrowUpRight, Check, TrendingUp, Video } from 'lucide-react';
import { CompletedByAvatars } from '../climbs/CompletedByAvatars';
import { formatGradeOpinion } from '../climb-log/gradeUtils';
import { cn } from '../../lib/utils';
import type { Climb, SendData } from '../../types/climb';
import type { CragReport } from '../../types/crag-report';

interface CragSectorClimbListProps {
  climbs: Climb[];
  selectedClimbId: string | null;
  onSelect: (climbId: string) => void;
  /** Open the list of who has sent this route. */
  onOpenSends: (climb: Climb) => void;
  /** Open the route's videos. Only reachable on rows that have some. */
  onOpenVideos: (climb: Climb) => void;
  /** Open your own goes on this route. Only reachable on rows that have some. */
  onOpenAttempts: (climb: Climb) => void;
  /** Leave the topo for the route's own page. */
  onOpenClimb: (climb: Climb) => void;
  /**
   * What has been reported wrong with each line, keyed by climb id. Only rows
   * with something open get a triangle — see below for why it is not a count.
   */
  reportsByClimb?: Map<string, CragReport[]>;
  /** Open a route's reports. Only reachable on rows that carry a triangle. */
  onOpenReports?: (climb: Climb) => void;
  /** Who is reading, so their own send leads the faces. */
  currentUserId?: string | null;
  /**
   * Bring the selected row into view. Off where the row is already in a fixed place
   * and scrolling the page for it would only move everything else.
   */
  scrollSelectedIntoView?: boolean;
}

/**
 * The senders, with the reader's own send first.
 *
 * Only three faces fit on a row, and on a popular line yours would fall off the
 * end of a list ordered by date — which is the one face that answers "have I
 * done this" at a glance. The rest keep the order they came in (newest first).
 */
function orderSenders(sends: SendData[], currentUserId?: string | null): SendData[] {
  if (!currentUserId) return sends;

  const mine = sends.filter((send) => String(send.attributes.user.id) === currentUserId);
  if (mine.length === 0) return sends;

  return [...mine, ...sends.filter((send) => String(send.attributes.user.id) !== currentUserId)];
}

/**
 * "Marco Rossi" → "Marco R."
 *
 * The credit rides under a row, where a full name would run into the width the
 * grade and the faces already spend — and a first name plus an initial is how
 * equippers get talked about at the cliff anyway. Only the last word is cut, so
 * a double first name survives ("Gian Luca Rossi" → "Gian R.").
 *
 * A single word is left whole: a guest typed in as "Ciccio" is a name, not a
 * first name missing its surname.
 */
function shortSetterName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] ?? '';

  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
}

/**
 * The sector's routes, in the order they are walked past.
 *
 * A row answers what it is called and at what grade, who has sent it (the
 * faces, which open the sends), whether anyone has filmed it (the clapperboard
 * and its count, which open the videos) and where you left off on it (the last
 * attempt, which opens your goes). Tapping the name lights the line on the
 * photo above. Underneath, who put the line up and the way out to its own page.
 *
 * Logging a go is not here — that is the page's single floating button, which
 * acts on whichever route is open, so twenty rows do not carry twenty buttons.
 */
export function CragSectorClimbList({
  climbs,
  selectedClimbId,
  onSelect,
  onOpenSends,
  onOpenVideos,
  onOpenAttempts,
  onOpenClimb,
  reportsByClimb,
  onOpenReports,
  currentUserId,
  scrollSelectedIntoView = true,
}: CragSectorClimbListProps) {
  const { t } = useTranslation();
  const selectedRef = useRef<HTMLLIElement>(null);

  // Picking a line on the photo has to bring its row into view — otherwise the
  // answer to "which route is this" is somewhere below the fold. `nearest`
  // leaves a row that is already visible where it is.
  useEffect(() => {
    if (!selectedClimbId || !scrollSelectedIntoView) return;
    selectedRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedClimbId, scrollSelectedIntoView]);

  if (climbs.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground">{t('cragProfile.sectorHasNoClimbs')}</p>
    );
  }

  return (
    <ul className="divide-y">
      {climbs.map((climb) => {
        const isSelected = climb.id === selectedClimbId;
        const senders = orderSenders(climb.sends ?? [], currentUserId).map((send) => send.attributes.user);
        const sendsCount = climb.sends?.length ?? climb.sends_count ?? 0;
        const videosCount = climb.videos_count ?? 0;
        // The most recent go of the reader's own, by the day it happened rather
        // than the day it was written down.
        const lastAttemptAt = (climb.user_attempts ?? [])
          .map((attempt) => attempt.attributes.attempted_at ?? attempt.attributes.created_at)
          .sort()
          .at(-1);
        const grade = climb.difficulty
          ? formatGradeOpinion(climb.difficulty, climb.average_grade_opinion ?? 0.5)
          : t('climbs.noGrade');
        // Only what is still true of the rock. A line whose loose block was
        // trundled last spring is a fixed line, and leaving a triangle on it
        // teaches everyone to stop reading the triangles.
        const climbReports = reportsByClimb?.get(climb.id) ?? [];
        const openReports = climbReports.filter((report) => report.status === 'accepted');
        // Only routes are measured, and only where somebody has done it — a
        // boulder has no length and most old lines were never paced out. Tested
        // for a number rather than against null: a cached payload from before
        // the field existed carries no `length` at all, and `undefined` would
        // otherwise print a bare unit with nothing in front of it.
        const routeLength = climb.type === 'Route' && typeof climb.length === 'number'
          ? climb.length
          : null;

        return (
          <li
            key={climb.id}
            ref={isSelected ? selectedRef : undefined}
            data-testid="crag-climb-row"
            className={cn(
              // The border is carried by every row, transparent when the row is
              // not the open one: coloured only on selection it would be added
              // width, and the list would step sideways as you move down it.
              'border-l-4 border-l-transparent px-3 py-2 transition-colors',
              isSelected ? 'border-l-primary bg-primary/5' : 'hover:bg-muted/50'
            )}
          >
            {/* Two columns, both stretched to the row's height so the right one
                can push its two halves apart. What the route *is* stacks down
                the left; what you can do about it sits down the right. */}
            <div className="flex gap-3">
              <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                <button
                  type="button"
                  onClick={() => onSelect(climb.id)}
                  className="flex w-full min-w-0 flex-col items-start gap-1 text-left"
                >
                  <span className="truncate font-medium">{climb.name}</span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      <TrendingUp className="h-3 w-3" />
                      {grade}
                    </span>
                    {/* How much rope the line eats, beside the grade because the
                        two are read together when picking what to get on. Plain
                        and small rather than a second chip: it is a detail of the
                        route, not a label with the weight of its grade. */}
                    {routeLength !== null && (
                      <span className="text-[11px] text-muted-foreground">
                        {t('cragProfile.lengthMeters', { value: routeLength })}
                      </span>
                    )}
                  </span>
                </button>

                {/* Where you left off on this line, and the way back to the goes
                    behind it. Only on routes you have actually been on. */}
                {lastAttemptAt && (
                  <button
                    type="button"
                    onClick={() => onOpenAttempts(climb)}
                    className="text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                  >
                    {t('cragProfile.lastAttempt', { date: format(new Date(lastAttemptAt), 'dd/MM') })}
                  </button>
                )}

                {/* Who put the line up, last: it is read after the grade and
                    the faces, not instead of them. No credit, no line — an
                    empty strip under every route would be height spent on
                    nothing. */}
                {climb.route_setter && (
                  <div className="flex w-full min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                    {/* Only accounts have a face. A guest equipper — which is
                        most of them on a cliff — is the name and nothing else. */}
                    {climb.route_setter.avatar_small_url && (
                      <img
                        src={climb.route_setter.avatar_small_url}
                        alt=""
                        data-testid="crag-climb-setter-avatar"
                        className="h-4 w-4 shrink-0 rounded-full object-cover"
                      />
                    )}
                    <span className="truncate">
                      {t('cragProfile.setBy', { name: shortSetterName(climb.route_setter.name) })}
                    </span>
                  </div>
                )}
              </div>

              {/* The right-hand column: the way out at the top, level with the
                  name it leaves for, and under it everything that acts on the
                  route in place. Stacked tight rather than spread down the
                  row — the left column is the taller of the two, and pushing
                  these two apart to match it left a hole between them. */}
              <div className="flex shrink-0 flex-col items-end gap-1">
                {/* The one control here that leaves the topo, which is why it
                    sits apart from the rest. */}
                <button
                  type="button"
                  onClick={() => onOpenClimb(climb)}
                  aria-label={t('cragProfile.openClimbPage', { name: climb.name })}
                  data-testid="crag-climb-open"
                  className="flex shrink-0 items-center rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-2">
                  {/* Something is wrong with this line. Ahead of the videos and
                      the faces because it is the one thing on the row that
                      changes whether you get on the route at all — and it shows
                      the whole list, resolved ones included, once opened. */}
                  {openReports.length > 0 && onOpenReports && (
                    <button
                      type="button"
                      onClick={() => onOpenReports(climb)}
                      aria-label={t('cragReports.showReports', { count: openReports.length })}
                      data-testid="crag-climb-report-warning"
                      className="flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-amber-600 hover:bg-amber-500/10 dark:text-amber-400"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      {openReports.length > 1 && openReports.length}
                    </button>
                  )}

                  {/* Only where there is something to watch: an icon reading
                      zero on every route is noise on a cliff where most lines
                      have no film. */}
                  {videosCount > 0 && (
                    <button
                      type="button"
                      onClick={() => onOpenVideos(climb)}
                      aria-label={t('cragProfile.showVideos', { count: videosCount })}
                      className="flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-xs text-muted-foreground hover:bg-muted"
                    >
                      <Video className="h-4 w-4" />
                      {videosCount}
                    </button>
                  )}

                  {/* The faces and the count are one target: both answer "who
                      has done this", and the drawer behind them is the same. */}
                  <button
                    type="button"
                    onClick={() => onOpenSends(climb)}
                    aria-label={t('cragProfile.showSends', { count: sendsCount })}
                    className="flex shrink-0 items-center gap-2 rounded-md px-1 py-1 hover:bg-muted"
                  >
                    <CompletedByAvatars users={senders} totalCount={sendsCount} />
                    <span className="text-xs text-muted-foreground">
                      {t('cragProfile.sendsCount', { count: sendsCount })}
                    </span>
                  </button>

                  {/* Your own standing on the route, which the faces do not
                      carry. */}
                  {climb.user_topped && (
                    <span
                      aria-label={t('cragProfile.youSentIt')}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-600 text-white"
                    >
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
